import axios from "axios";
import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import useT from "../../i18n/useT";
import core from "../../core";

const isStaffRole = (role) => role === "admin" || role === "moderator" || role === "dispecher";

const Chat = () => {
    const t = useT();
    const user = useSelector((state) => state.user);
    const role = user.role;
    const staff = isStaffRole(role);

    const [conversations, setConversations] = useState([]);
    const [activeDealer, setActiveDealer] = useState(staff ? null : user.id);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const bottomRef = useRef(null);
    const pollRef = useRef(null);

    const loadConversations = useCallback(() => {
        axios.get("/api/chat/conversations").then((res) => setConversations(res.data.data || [])).catch(() => {});
    }, []);

    const loadMessages = useCallback((dealerId) => {
        if (dealerId == null) return;
        axios.get(`/api/chat/${dealerId}/messages`).then((res) => setMessages(res.data.data || [])).catch(() => {});
    }, []);

    useEffect(() => { loadConversations(); }, [loadConversations]);

    useEffect(() => {
        if (activeDealer == null) return;
        loadMessages(activeDealer);
        pollRef.current = setInterval(() => loadMessages(activeDealer), 12000);
        return () => clearInterval(pollRef.current);
    }, [activeDealer, loadMessages]);

    useEffect(() => { bottomRef.current && bottomRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const send = (e) => {
        e.preventDefault();
        const body = draft.trim();
        if (!body || activeDealer == null) return;
        setDraft("");
        axios.post(`/api/chat/${activeDealer}/messages`, { body })
            .then(() => { loadMessages(activeDealer); loadConversations(); })
            .catch(() => {});
    };

    const dealerName = (c) => (c.name ? `${c.name} ${c.surname || ""}`.trim() : c.username || `#${c.dealer_id}`);

    const thread = (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                {messages.length === 0 ? (
                    <div style={{ opacity: 0.55, textAlign: "center", marginTop: "30px", fontSize: "13px" }}>{t("chat.empty")}</div>
                ) : messages.map((m) => {
                    const mine = Number(m.sender_id) === Number(user.id);
                    return (
                        <div key={m.id} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "75%" }}>
                            <div style={{ background: mine ? "#2f80ed" : "var(--surface-2, rgba(127,127,127,0.12))", color: mine ? "#fff" : "inherit", padding: "8px 12px", borderRadius: "12px", fontSize: "14px", wordBreak: "break-word" }}>{m.body}</div>
                            <div style={{ fontSize: "10px", opacity: 0.5, marginTop: "2px", textAlign: mine ? "right" : "left" }}>
                                {mine ? t("chat.you") : (isStaffRole(m.sender_role) ? t("chat.staff") : "")} · {m.created_at ? core.formdatDate(m.created_at, "dd-mm-yyyy") : ""}
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>
            <form onSubmit={send} style={{ display: "flex", gap: "8px", padding: "12px", borderTop: "1px solid var(--border)" }}>
                <input className="form-control" style={{ flex: 1 }} placeholder={t("chat.typeMessage")} value={draft} onChange={(e) => setDraft(e.target.value)} />
                <button type="submit" className="btn btn-primary">{t("chat.send")}</button>
            </form>
        </div>
    );

    // Dealer: single thread with support.
    if (!staff) {
        return (
            <div className="page-content-main-container">
                <h2 className="page-title">{t("chat.withSupport")}</h2>
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", height: "68vh", overflow: "hidden" }}>
                    {thread}
                </div>
            </div>
        );
    }

    // Staff: conversation list + thread.
    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("chat.title")}</h2>
            <div style={{ display: "flex", gap: "14px", height: "70vh", minHeight: 0 }}>
                <div style={{ flex: "0 0 280px", maxWidth: "40%", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", overflowY: "auto" }}>
                    <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)", fontWeight: 600, fontSize: "14px" }}>{t("chat.conversations")}</div>
                    {conversations.length === 0 ? (
                        <div style={{ padding: "18px 14px", opacity: 0.6, fontSize: "13px" }}>{t("chat.noConversations")}</div>
                    ) : conversations.map((c) => (
                        <div key={c.dealer_id} onClick={() => setActiveDealer(c.dealer_id)} style={{ padding: "11px 14px", borderBottom: "1px solid var(--border)", cursor: "pointer", background: Number(activeDealer) === Number(c.dealer_id) ? "rgba(47,128,237,0.10)" : "transparent" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontWeight: 600, fontSize: "13px" }}>{dealerName(c)}</span>
                                {Number(c.unread) > 0 && <span style={{ background: "#dc3545", color: "#fff", fontSize: "10px", borderRadius: "8px", padding: "1px 6px" }}>{c.unread}</span>}
                            </div>
                            {c.last_body && <div style={{ fontSize: "12px", opacity: 0.65, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.last_body}</div>}
                        </div>
                    ))}
                </div>
                <div style={{ flex: 1, minWidth: 0, background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}>
                    {activeDealer == null ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", opacity: 0.55, fontSize: "14px" }}>{t("chat.selectConversation")}</div>
                    ) : thread}
                </div>
            </div>
        </div>
    );
};

export default Chat;
